import{ useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert } from 'react-bootstrap';
import { ROUTES } from '../constants/routes';
import { useBrand } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { searchService } from '../services/searchService';
import { billService } from '../services/billService';
import { formatAddress } from '../utils/formatters';
import CustomerContactSection from '../components/customer/CustomerContactSection';
import CustomerDetailCard from '../components/customer/CustomerDetailCard';
import AccountDetailCard from '../components/customer/AccountDetailCard';
import BillingStatementsCard from '../components/customer/BillingStatementsCard';
import UsageHistorySection from '../components/customer/UsageHistorySection';

const CustomerDetailPage = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useUI();

  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState('');

  // Bill selection
  const [selectedBill, setSelectedBill] = useState('');

  useEffect(() => {
    const loadCustomer = async () => {
      showLoading('Loading customer details...');
      try {
        const response = await searchService.getCustomerDetail(accountId);
        if (response.success) {
          setCustomer(response.customer);
        } else {
          setError(response.error || 'Failed to load customer details.');
        }
      } catch (err) {
        console.error('Error loading customer:', err);
        setError('An error occurred while loading customer details.');
      } finally {
        hideLoading();
      }
    };
    loadCustomer();
  }, [accountId, showLoading, hideLoading]);

  if (!customer && !error) {
    // Display nothing when data is not ready
    return <></>;
  }

  const handleViewBill = () => {
    if (!selectedBill) return;

    const [billId] = selectedBill.split(':');
    billService.viewBill(billId);
  };

  const handleDownloadAllBills = () => {
    billService.downloadAllBills();
  };

  if (error) {
    return (
      <Alert variant="danger">{error}</Alert>
    );
  }

  const serviceAddress = formatAddress(customer?.latestServiceAgreement?.premise);

  return (
    <Container id="customer-detail-page">
      {/* Page Header with Customer Details title and Update Benefit button */}
      <div className="d-flex justify-content-between mb-3">
        <Col md={7} className="text-md-end"><h2>Customer Details</h2></Col>
        <Col md={2}>
          {customer?.eligibleForAssistBenefits && (
            <Button variant="primary" onClick={() => navigate(ROUTES.BENEFIT_UPDATE)}>
              Update Benefit
            </Button>
          )}
        </Col>
      </div>

        <Row>
          {/* Left Column: Customer Detail + Customer Contact */}
          <Col lg={6}>
            <CustomerDetailCard customer={customer} serviceAddress={serviceAddress} />

            {/* Customer Contact Section (moved from right) */}
            <CustomerContactSection state={customer?.latestServiceAgreement?.premise?.state} />
          </Col>

          {/* Right Column: Account Detail + Billing Statements */}
          <Col lg={6}>
            <AccountDetailCard customer={customer} />

            <BillingStatementsCard
              bills={customer?.bills}
              selectedBill={selectedBill}
              onBillSelect={setSelectedBill}
              onViewBill={handleViewBill}
              onDownloadAll={handleDownloadAllBills}
            />
          </Col>
        </Row>

      <UsageHistorySection accountId={accountId} />
    </Container>
  );
};

export default CustomerDetailPage;